import { useEffect, useState } from "react";
import RootCockpit from "./RootCockpit";
import AiEnginePanel from "./AiEnginePanel";
import FleetPanel from "./FleetPanel";
import ConnectorsPanel from "./ConnectorsPanel";
import GestionPanel from "./GestionPanel";
import {
  addTrustedDevice, analyzeFractalExponential, analyzeUnknown, clearRootSession,
  confirmTotp, enrollTotp, getBiometricStatus, getReadiness, getRootStatus, hasRootSession,
  loginRoot, registerPasskey, rootAnalyze, rootFractal, stepUp,
  adminConverse, getCapabilities, getFabricStatus, getDeadLetters, replayEvent,
  listApprovals, approveAction, rejectAction,
  runGovernor, getProposals, getGovernorInbox,
  getFabricSystems, getFounderTop,
} from "@/lib/rootConsole";

const box = { background: "#0b0f14", color: "#d7e0ea", minHeight: "100vh", padding: 24,
  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" };
const card = { background: "#121924", border: "1px solid #22303f", borderRadius: 10,
  padding: 16, marginBottom: 16 };
const btn = { background: "#1f6feb", color: "#fff", border: "none", borderRadius: 8,
  padding: "8px 14px", cursor: "pointer", marginRight: 8 };
const input = { background: "#0b0f14", color: "#d7e0ea", border: "1px solid #22303f",
  borderRadius: 8, padding: 10, width: "100%", marginTop: 8 };

export default function RootConsole() {
  const [status, setStatus] = useState(null);
  const [err, setErr] = useState(null);
  const [otpUri, setOtpUri] = useState(null);
  const [code, setCode] = useState("");
  const [loginCode, setLoginCode] = useState("");
  const [rooted, setRooted] = useState(hasRootSession());
  const [log, setLog] = useState([]);
  const [problem, setProblem] = useState("");
  const [bio, setBio] = useState(null);
  const [stepCode, setStepCode] = useState("");
  const [deviceId, setDeviceId] = useState("");
  const [adminMsg, setAdminMsg] = useState("");
  const [adminLog, setAdminLog] = useState([]);
  const [caps, setCaps] = useState([]);
  const [fabric, setFabric] = useState(null);
  const [dlq, setDlq] = useState([]);
  const [approvals, setApprovals] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [govInbox, setGovInbox] = useState([]);
  const [systems, setSystems] = useState(null);
  const [founderTop, setFounderTop] = useState(null);

  const refresh = async () => {
    try { setStatus(await getRootStatus()); setErr(null); }
    catch (e) { setErr(e?.response?.data?.detail || "Connectez-vous d'abord en tant que Founder (/login)."); }
  };
  useEffect(() => { refresh(); }, []);

  const wrap = (fn) => async () => { try { await fn(); setErr(null); } catch (e) { setErr(e?.response?.data?.detail || String(e)); } };

  const doEnroll = wrap(async () => { const r = await enrollTotp(); setOtpUri(r.otpauth_uri); });
  const doConfirm = wrap(async () => { await confirmTotp(code); await refresh(); setOtpUri(null); });
  const doRegister = wrap(async () => { await registerPasskey(); await refresh(); });
  const doLogin = wrap(async () => { await loginRoot(loginCode); setRooted(true); });
  const push = (who, txt) => setLog((l) => [...l, { who, txt }]);
  const doAnalyze = wrap(async () => { const r = await rootAnalyze(problem); push("Tuluss", "Analyse: " + r.council.synthesis); });
  const doFractal = wrap(async () => { const r = await rootFractal(problem); push("Tuluss", `Fractale: ${r.branch_count} branches, prof.${r.max_depth_reached}, ${r.contradictions.length} contradiction(s). ${r.synthesis}`); });

  const loadBio = wrap(async () => { setBio(await getBiometricStatus()); });
  const doAnalyzeUnknown = wrap(async () => {
    const r = await analyzeUnknown(problem || "opportunité inconnue à évaluer pour La BASE");
    push("Tuluss", `Demande inconnue → ${r.axes.length} axes, ${r.branch_count} branches, ${r.reused_count} leçon(s) réutilisée(s), confiance ${r.confidence} [${r.readiness}]. ${r.synthesis}`);
  });
  const doFractalExponential = wrap(async () => {
    const r = await analyzeFractalExponential(problem || "problème inconnu critique et incertain à résoudre", "critical", 0.9);
    const fo = r.failover || {};
    const q = r.quantum || {};
    push("Tuluss", `Fractale exponentielle [${r.effort?.mode}] → profondeur ${r.max_depth_reached}/${r.depth_used}, ${r.branch_count} branches, ${r.parallel_batches} niveaux, concurrence ${r.max_concurrency}, budget //${r.parallelism_budget}. Failover modèle ${fo.model?.failed}→${fo.model?.switched_to}, worker ${fo.worker?.failed}→${fo.worker?.switched_to} (identité préservée). Quantique: ${q.candidate ? q.provider + " [" + q.status + "]" : "non pertinent"}. ${r.reused_count} leçon(s) réutilisée(s), confiance ${r.confidence}. ${r.synthesis}`);
  });
  const doStepUpAddDevice = wrap(async () => {
    if (!deviceId.trim()) throw new Error("device_id requis");
    const token = await stepUp("add_device", stepCode);   // WebAuthn natif + TOTP
    await addTrustedDevice(deviceId.trim(), "root-console", token);
    setStepCode(""); setDeviceId("");
    await loadBio();
    push("Tuluss", `Appareil de confiance ajouté après step-up (action critique) : ${deviceId}.`);
  });

  const pushAdmin = (who, txt) => setAdminLog((l) => [...l, { who, txt }]);
  const refreshFabric = wrap(async () => {
    setFabric(await getFabricStatus());
    setDlq((await getDeadLetters()).dead_letters || []);
    setApprovals((await listApprovals()).pending || []);
  });
  const doAdmin = wrap(async () => {
    if (!adminMsg.trim()) return;
    pushAdmin("Founder", adminMsg);
    const r = await adminConverse(adminMsg);
    pushAdmin("Tuluss", r.reply || JSON.stringify(r));
    setAdminMsg("");
    await refreshFabric();
  });
  const loadCaps = wrap(async () => { setCaps((await getCapabilities()).capabilities || []); });
  const doReplay = (id) => wrap(async () => { await replayEvent(id); await refreshFabric(); })();
  const doApprove = (id) => wrap(async () => { await approveAction(id); await refreshFabric(); })();
  const doReject = (id) => wrap(async () => { await rejectAction(id, "refusé depuis la console"); await refreshFabric(); })();

  const refreshGovernor = wrap(async () => {
    setProposals((await getProposals()).proposals || []);
    setGovInbox((await getGovernorInbox()).inbox || []);
  });
  const doRunGovernor = wrap(async () => {
    const r = await runGovernor();
    pushAdmin("Tuluss", `Cycle de gouvernance : ${r.detected} signal(aux) détecté(s), ${r.executed} action(s) exécutée(s), ${r.approvals} approbation(s) requise(s), ${r.explored} univers explorés.`);
    await refreshGovernor();
    await refreshFabric();
  });
  const refreshSystems = wrap(async () => {
    setSystems(await getFabricSystems());
    setFounderTop(await getFounderTop());
  });

  return (
    <div style={rooted ? { background: "#080b11", minHeight: "100vh" } : box} data-testid="root-console">
      {!rooted && (
        <>
          <h1 style={{ color: "#58a6ff" }}>TULUSS · Root Console</h1>
          <p style={{ color: "#8b98a5" }}>Surface unique du Founder (ADMIN_PRINCIPAL). WebAuthn + TOTP requis.</p>
          {err && <div style={{ ...card, borderColor: "#f85149", color: "#ff7b72" }} data-testid="root-error">{err}</div>}
          <div style={card} data-testid="root-auth-panel">
            <h3>1 · Enrôlement &amp; session Root</h3>
            <div style={{ color: "#8b98a5", marginBottom: 8 }}>
              Founder: {status?.founder_email || "?"} · Passkey: {String(status?.passkey_registered)} · TOTP: {String(status?.totp_enabled)}
            </div>
            <button style={btn} onClick={doEnroll} data-testid="btn-enroll-totp">Enrôler TOTP</button>
            <button style={btn} onClick={doRegister} data-testid="btn-register-passkey">Enregistrer Passkey</button>
            {otpUri && (
              <div style={{ marginTop: 10 }}>
                <div style={{ color: "#8b98a5", wordBreak: "break-all" }}>{otpUri}</div>
                <input style={input} placeholder="Code TOTP (6 chiffres)" value={code} onChange={(e) => setCode(e.target.value)} data-testid="input-totp-confirm" />
                <button style={{ ...btn, marginTop: 8 }} onClick={doConfirm} data-testid="btn-confirm-totp">Confirmer TOTP</button>
              </div>
            )}
            <hr style={{ borderColor: "#22303f", margin: "16px 0" }} />
            <input style={input} placeholder="Code TOTP pour ouvrir la session Root" value={loginCode} onChange={(e) => setLoginCode(e.target.value)} data-testid="input-root-login-code" />
            <button style={{ ...btn, marginTop: 8 }} onClick={doLogin} data-testid="btn-login-root">Ouvrir session Root (Passkey + TOTP)</button>
          </div>
        </>
      )}

      {rooted && (
        <>
          <RootCockpit
            onAuthError={() => { clearRootSession(); setRooted(false); }}
            onLogout={() => { clearRootSession(); setRooted(false); }}
          />

          <section style={{ padding: "0 24px 8px" }} data-testid="gestion-section">
            <GestionPanel />
          </section>

          <section className="ops-adv" data-testid="ops-advanced">
            <details>
              <summary>Ops avancées — diagnostic &amp; gouvernance technique</summary>
              <div className="ops-adv__inner">
                {err && <div style={{ ...card, borderColor: "#f85149", color: "#ff7b72" }} data-testid="root-error">{err}</div>}

                <AiEnginePanel />

                <FleetPanel />

                <ConnectorsPanel />


                <div style={card} data-testid="root-analysis">
                  <h3>Analyse cognitive</h3>
                  <input style={input} placeholder="Problème à analyser" value={problem} onChange={(e) => setProblem(e.target.value)} data-testid="input-problem" />
                  <div style={{ marginTop: 8 }}>
                    <button style={btn} onClick={doAnalyze} data-testid="btn-analyze">Analyse normale</button>
                    <button style={btn} onClick={doFractal} data-testid="btn-fractal">Analyse fractale</button>
                    <button style={btn} onClick={doAnalyzeUnknown} data-testid="btn-analyze-unknown">Demande inconnue (E2E)</button>
                    <button style={btn} onClick={doFractalExponential} data-testid="btn-fractal-exponential">Fractale exponentielle (PROD 10)</button>
                  </div>
                  {log.length > 0 && (
                    <div style={{ maxHeight: 220, overflowY: "auto", marginTop: 10 }} data-testid="analysis-log">
                      {log.map((m, i) => (
                        <div key={i} style={{ marginBottom: 6, fontSize: 13 }}>
                          <b style={{ color: m.who === "Tuluss" ? "#58a6ff" : "#7ee787" }}>{m.who}:</b> {m.txt}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div style={card} data-testid="root-biometric">
                  <h3>Identité biométrique &amp; Step-up</h3>
                  <button style={btn} onClick={loadBio} data-testid="btn-load-biometric">Charger l&apos;état biométrique</button>
                  {bio && (
                    <div style={{ marginTop: 10, fontSize: 13 }} data-testid="biometric-status">
                      <div style={{ color: "#8b98a5", marginBottom: 6 }}>
                        Identité : <b style={{ color: "#7ee787" }}>{bio.identity_type}</b> · Fabric chiffré : {String(bio.configured)}
                      </div>
                      {Object.entries(bio.providers || {}).map(([name, p]) => (
                        <div key={name}>
                          <b>{name}</b> — runtime {p.runtime} · <span style={{ color: p.readiness === "REAL_RUNTIME_VERIFIED" ? "#7ee787" : "#e3b341" }}>{p.readiness}</span>
                        </div>
                      ))}
                      <div style={{ color: "#8b98a5", marginTop: 6 }}>
                        Appareils de confiance : {(bio.devices || []).length} · Enrôlements : {(bio.enrollments || []).length}
                      </div>
                    </div>
                  )}
                  <hr style={{ borderColor: "#22303f", margin: "14px 0" }} />
                  <div style={{ color: "#8b98a5", marginBottom: 6 }}>
                    Action critique (step-up obligatoire) : ajouter un appareil de confiance.
                  </div>
                  <input style={input} placeholder="Identifiant appareil (device_id)" value={deviceId} onChange={(e) => setDeviceId(e.target.value)} data-testid="input-device-id" />
                  <input style={input} placeholder="Code TOTP pour le step-up" value={stepCode} onChange={(e) => setStepCode(e.target.value)} data-testid="input-stepup-code" />
                  <button style={{ ...btn, marginTop: 8 }} onClick={doStepUpAddDevice} data-testid="btn-stepup-add-device">Step-up (Passkey + TOTP) &amp; ajouter</button>
                </div>

                <div style={card} data-testid="root-admin">
                  <h3>Administration de La BASE (Tuluss)</h3>
                  <div style={{ color: "#8b98a5", marginBottom: 8 }}>
                    Parle naturellement : « ajoute un véhicule scooter », « désactive le partenaire X »,
                    « retire ce produit … », « montre-moi les commandes problématiques », « change la
                    tarification globale » (→ approbation requise).
                  </div>
                  <div style={{ maxHeight: 220, overflowY: "auto", marginBottom: 8 }} data-testid="admin-log">
                    {adminLog.map((m, i) => (
                      <div key={i} style={{ marginBottom: 6 }}>
                        <b style={{ color: m.who === "Tuluss" ? "#58a6ff" : "#7ee787" }}>{m.who}:</b> {m.txt}
                      </div>
                    ))}
                  </div>
                  <input style={input} placeholder="Demande d'administration à Tuluss" value={adminMsg} onChange={(e) => setAdminMsg(e.target.value)} data-testid="input-admin" />
                  <div style={{ marginTop: 8 }}>
                    <button style={btn} onClick={doAdmin} data-testid="btn-admin-send">Exécuter via Tuluss</button>
                    <button style={{ ...btn, background: "#30363d" }} onClick={loadCaps} data-testid="btn-admin-caps">Capacités</button>
                  </div>
                  {caps.length > 0 && (
                    <div style={{ marginTop: 10, fontSize: 12, color: "#8b98a5" }} data-testid="admin-caps">
                      {caps.map((c) => (
                        <div key={c.name}>
                          <b style={{ color: c.kind === "fundamental" ? "#e3b341" : "#7ee787" }}>{c.name}</b>
                          {" "}[{c.kind}] — {c.desc}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div style={card} data-testid="root-fabric">
                  <h3>Fabric LIVE &amp; Approbations</h3>
                  <button style={btn} onClick={refreshFabric} data-testid="btn-fabric-refresh">Rafraîchir l&apos;état</button>
                  {fabric && (
                    <div style={{ marginTop: 10, fontSize: 13 }} data-testid="fabric-status">
                      <div>Dispatcher permanent : <b style={{ color: fabric.live ? "#7ee787" : "#ff7b72" }}>{fabric.live ? "LIVE" : "OFF"}</b></div>
                      <div style={{ color: "#8b98a5" }}>
                        pending {fabric.counts?.pending} · done {fabric.counts?.done} ·
                        dead-letter {fabric.counts?.dead_letter} · deferred {fabric.counts?.deferred} ·
                        total {fabric.counts?.total}
                      </div>
                      <div style={{ color: "#8b98a5" }}>Sources : {(fabric.sources || []).join(", ")}</div>
                    </div>
                  )}
                  {approvals.length > 0 && (
                    <div style={{ marginTop: 12 }} data-testid="fabric-approvals">
                      <div style={{ color: "#e3b341", marginBottom: 6 }}>Actions en attente d&apos;approbation :</div>
                      {approvals.map((a) => (
                        <div key={a.id} style={{ marginBottom: 6, fontSize: 13 }}>
                          <span style={{ color: "#d7e0ea" }}>{a.title} ({a.kind})</span>
                          <button style={{ ...btn, marginLeft: 8, padding: "4px 8px", background: "#238636" }} onClick={() => doApprove(a.id)} data-testid={`btn-approve-${a.id}`}>Approuver</button>
                          <button style={{ ...btn, padding: "4px 8px", background: "#da3633" }} onClick={() => doReject(a.id)} data-testid={`btn-reject-${a.id}`}>Refuser</button>
                        </div>
                      ))}
                    </div>
                  )}
                  {dlq.length > 0 && (
                    <div style={{ marginTop: 12 }} data-testid="fabric-dlq">
                      <div style={{ color: "#ff7b72", marginBottom: 6 }}>Dead-letter queue :</div>
                      {dlq.map((d) => (
                        <div key={d.event_id} style={{ marginBottom: 6, fontSize: 12, color: "#8b98a5" }}>
                          {d.source}/{d.kind} — {d.last_error?.slice(0, 60)}
                          <button style={{ ...btn, marginLeft: 8, padding: "4px 8px" }} onClick={() => doReplay(d.event_id)} data-testid={`btn-replay-${d.event_id}`}>Rejouer</button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div style={card} data-testid="root-systems">
                  <h3>Systèmes LIVE &amp; TOP 3 Founder</h3>
                  <button style={btn} onClick={refreshSystems} data-testid="btn-systems-refresh">Rafraîchir</button>
                  {systems && (
                    <div style={{ marginTop: 10, fontSize: 13 }} data-testid="systems-list">
                      <div style={{ color: "#8b98a5", marginBottom: 6 }}>
                        {systems.live_count}/{systems.total} systèmes LIVE — ARCHITECTURE_READY : {(systems.architecture_ready || []).join(", ") || "aucun"}
                      </div>
                      {systems.systems.map((s) => (
                        <div key={s.source} style={{ marginBottom: 3 }}>
                          <b style={{ color: s.status === "LIVE" ? "#7ee787" : "#e3b341" }}>{s.status}</b>{" "}
                          <span style={{ color: "#d7e0ea" }}>{s.source}</span>{" "}
                          <span style={{ color: "#8b98a5" }}>({s.events_seen} événements)</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {founderTop && (
                    <div style={{ marginTop: 12 }} data-testid="founder-top3">
                      <div style={{ color: "#58a6ff", marginBottom: 6 }}>TOP 3 par impact réel :</div>
                      {!founderTop.sufficient_data && <div style={{ color: "#8b98a5" }}>{founderTop.note}</div>}
                      {(founderTop.top || []).map((t, i) => (
                        <div key={t.id} style={{ marginBottom: 6, fontSize: 12 }}>
                          <b style={{ color: "#d7e0ea" }}>{i + 1}. {t.type}</b> — {t.title}{" "}
                          <span style={{ color: "#7ee787" }}>[priorité {t.priority_score}]</span>{" "}
                          <span style={{ color: "#8b98a5" }}>impact: {t.impact?.basis}{t.impact?.value != null ? ` ${t.impact.value} ${t.impact.unit || ""}` : ""}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div style={card} data-testid="root-governor">
                  <h3>Gouverneur cognitif proactif (24/7)</h3>
                  <div style={{ color: "#8b98a5", marginBottom: 8 }}>
                    Tuluss observe La BASE en continu et vient spontanément vers toi : détection →
                    analyse → conséquences → proposition → action préparée.
                  </div>
                  <button style={btn} onClick={doRunGovernor} data-testid="btn-governor-run">Lancer un cycle</button>
                  <button style={{ ...btn, background: "#30363d" }} onClick={refreshGovernor} data-testid="btn-governor-refresh">Rafraîchir</button>
                  {govInbox.length > 0 && (
                    <div style={{ marginTop: 12 }} data-testid="governor-inbox">
                      <div style={{ color: "#58a6ff", marginBottom: 6 }}>Signaux spontanés de Tuluss :</div>
                      {govInbox.slice(0, 8).map((n) => (
                        <div key={n.id} style={{ marginBottom: 5, fontSize: 13 }}>
                          <b style={{ color: n.level === "CRITICAL" ? "#ff7b72" : n.level === "APPROVAL_REQUIRED" ? "#e3b341" : "#7ee787" }}>[{n.level}]</b>{" "}
                          {n.title} — <span style={{ color: "#8b98a5" }}>{n.body}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {proposals.length > 0 && (
                    <div style={{ marginTop: 12 }} data-testid="governor-proposals">
                      <div style={{ color: "#8b98a5", marginBottom: 6 }}>Propositions préparées :</div>
                      {proposals.slice(0, 8).map((p) => (
                        <div key={p.id} style={{ marginBottom: 8, fontSize: 12, borderLeft: "2px solid #30363d", paddingLeft: 8 }}>
                          <div style={{ color: "#d7e0ea" }}><b>{p.type}</b> — {p.title}</div>
                          <div style={{ color: "#8b98a5" }}>{p.analysis}</div>
                          <div style={{ color: "#8b98a5" }}>Conséquences : {p.consequences}</div>
                          <div style={{ color: "#7ee787" }}>Proposition : {p.proposal}</div>
                          {p.universe && <div style={{ color: "#58a6ff" }}>Univers ouverts : {p.universe.count} (+{p.universe.sub_count} sous-univers)</div>}
                          <div style={{ color: p.requires_approval ? "#e3b341" : p.executed ? "#7ee787" : "#8b98a5" }}>
                            {p.requires_approval ? "⛔ Approbation Founder requise" : p.executed ? `✅ Exécutée (vérifiée=${p.verified})` : "Notée"}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </details>
          </section>
        </>
      )}
    </div>
  );
}
